import { Component, OnInit, Input, Optional, Inject, OnDestroy } from '@angular/core';
import { PershingTranslationService } from '@pershing/pershing-core';
import { BalancesResponse, CustomGridHeader } from '../../model/index';
import { BalancesDetailsChildrowsComponent } from './balances-details-childrows.component';
import { CellRenderKeyComponent } from '../supporting/cellRender-key.component';
import { CellRenderValueComponent } from '../supporting/cellRender-value.component';
import { NxiBalancesGridHeaderComponent } from '../supporting/nxi-balances-grid-header.component';
import { BalancesLabels } from '../../i18n/balances-labels';
import { BalancesService } from '../../services/balances.services';
import { ClientSideRowModelModule, GridOptions, Module } from '@ag-grid-community/all-modules';
import { RowGroupingModule } from '@ag-grid-enterprise/all-modules';
import { CellRenderValueMobileComponent } from '../supporting/cellRender-value-mobile.component';
import { BalancesExportUtil } from '../../utils/balances-export-util';
import { Chart } from 'angular-highcharts';
import { Options } from 'highcharts';
import { ChartValues } from '../../model/chart-model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';


@Component({
    selector: 'balances-details-custodian-grid',
    templateUrl: './balances-details-custodian-grid.component.html'
})
export class BalancesDetailsCustodianGridComponent implements OnInit, OnDestroy {
    deviceType: any;
    bd: any;
    bs: any;
    @Input() set balancesResponse(balancesResponse: BalancesResponse) {
        if (balancesResponse) {
            this.createBalanceRowData(balancesResponse);
        }
    }
    isLoading: boolean = true; // initial load
    isLoadingBalanceData: boolean = true; // subsequent load when changing acc
    hoverTooltip: string = 'Detail - Account Balances';
    @Input() rowData: any[];
    gridOptions: GridOptions;
    public modules: Module[] = [ClientSideRowModelModule, RowGroupingModule];
    customHeaders: any;
    columnDefs: any[] = [
        {
            headerName: BalancesLabels.DETAIL_LABELS_BALANCESTODAYCHNG,
            field: 'value',
            headerClass: 'ag-right-aligned-header ag-cell-label-container bal-BalancesDetails_BalancesTodaysChange padding-top-0',
            cellClass: 'right-align bal-BalancesDetails_BalancesTodaysChange',
            cellRendererFramework: CellRenderValueComponent
        }
    ];
    piechart: Chart;
    unsubscribe: Subject<void> = new Subject<void>();

    constructor(
        private translationService: PershingTranslationService,
        private balancesService: BalancesService,
        @Optional() @Inject('AppSettings') appSettings: any
    ) { 
        this.deviceType = appSettings.deviceType;
    }

    initGrid() {
        let rowHeight = 50;
        let headerHeight = 44;
        if(this.deviceType === "mobile"){
            this.initCustomHeaders();
            rowHeight = 75;
            headerHeight = 70;
            this.columnDefs = [
                {
                    headerName: BalancesLabels.DETAIL_LABELS_BNYMELLONNA,
                    field: 'value',
                    headerClass: 'bal-BalancesDetails_BalancesTodaysChange padding-top-0',
                    cellClass: 'bal-BalancesDetails_BalancesTodaysChange',
                    cellStyle: { textAlign: 'right'},
                    cellRendererFramework: CellRenderValueMobileComponent
                }
            ];
        }
        this.gridOptions = {
            suppressContextMenu: true,
            columnDefs: this.columnDefs,
            suppressMovableColumns: true,
            overlayLoadingTemplate: '<div class="progress-spinner"></div>',
            domLayout: 'autoHeight',
            autoGroupColumnDef: { 
                headerName: '',
                cellClass: 'bal-BalancesDetails_BalancesHighchartLabel',
                cellRendererParams: { innerRendererFramework: CellRenderKeyComponent, suppressCount: true }
            },
            onGridSizeChanged: () => {
                if (this.gridOptions.api) { this.gridOptions.api.sizeColumnsToFit(); }
            },
            onGridReady: ($event) => {
                this.gridOptions.api.sizeColumnsToFit();
            },
            headerHeight: headerHeight,
            rowHeight: rowHeight,
            treeData: true,
            getDataPath: function (data){
                return data.heirarchy;
            }
        };
        this.gridOptions.defaultColDef = {
            headerComponentFramework: NxiBalancesGridHeaderComponent,
            headerComponentParams: this.customHeaders,
           // tooltip: (params) => this.hoverTooltip
        };
        if(this.deviceType === "mobile") {
            this.gridOptions.autoGroupColumnDef = { 
                headerName: '',
                cellClass: 'bal-BalancesDetails_BalancesHighchartLabel',
                minWidth: 223,
                cellRendererParams: { innerRendererFramework: CellRenderKeyComponent, suppressCount: true }
            }
        }
    }
    doesDataFlower(data: any) {
        return (data && data.children && data.children.length > 0);
    }

    getFullWidthCellRenderer() {
        return BalancesDetailsChildrowsComponent;
    }

    isFullWidthCell(rowNode: any) {
        return rowNode.level === 1;
    }

    ngOnInit() {
        this.translationService.addTranslationFolder('@pershing/nxi-ng-balances', 'node_modules/@pershing/nxi-ng-balances/lib');
        this.initGrid();
        this.balancesService.fetchingBalancesData$.pipe(takeUntil(this.unsubscribe)).subscribe(fetching => {
            this.isLoadingBalanceData = !!fetching ? fetching : false;
        });
    }

    initChart() {
        const donutChartOptions: Options = {
          chart: {
            type: 'pie',
            margin: [0, 5, 0, 5],
            spacingTop: 0,
            spacingBottom: 0,
            spacingLeft: 0,
            spacingRight: 0
          },
          plotOptions: {
            pie: {
              shadow: false,
              slicedOffset: 0,
              dataLabels: {
                connectorWidth: 0,
                enabled: false
              },
              size: '100%',
              innerSize: '100%',
              borderWidth: 10,
              borderColor: null,
              showInLegend: false
            }
          },
          exporting: {
            enabled: false
          },
          title: {
            text: '',
            align: 'center',
            verticalAlign: 'middle',
            margin: 0,
            y: 0,
            x: 0
          },
          credits: {
            enabled: false
          },
          tooltip: {
            pointFormat: ''
          },
          series: [
            {
              ignoreHiddenPoint: false,
              enableMouseTracking: false,
              data: this.getChartData(),
              type: 'pie'
            }
          ]
        };
    
        this.piechart = new Chart(donutChartOptions);
    }

    getChartData() {
        let chartdata = [];
        let index = 0;
        let total = 0;
        let totalVal = 0;
        this.rowData.forEach(obj => {
            if(obj.value > 0 && obj.name != "DETAIL.keyValues.totalAccount" && obj.heirarchy.length == 1) {
                totalVal += obj.value;
            }
        });
        this.rowData.forEach(element => {
            let percentageVal = 0;
            if (element.value > 0 && element.name != "DETAIL.keyValues.totalAccount" && element.heirarchy.length == 1) {
                percentageVal = (100 * element.value) / totalVal;
            }
            let c = new ChartValues(percentageVal, element.name, true);
            if (c.y > 0) {
                chartdata.push(c);
            }
            element.highchartClass = 'highcharts-color-' + ((index * 2 < 10) ? (index * 2) : (index * 2 - 10));
            index++;
            total += percentageVal;
        });
        //inserting invisible spacers into pie chart
        let length = chartdata.length;
        if (length > 1) {
          let hiddenY = total * 0.04;
          let hidden = { visible: false, y: hiddenY };
          for (let index = 1; index < length * 2; index += 2) {
            chartdata.splice(index, 0, hidden);
          }
        }
        console.log("chartdata is =====",chartdata);
        return chartdata;
    }

    createBalanceRowData(rsp: BalancesResponse) {
        if (!rsp || rsp.response.balances.balancesSummary === null || rsp.response.balances.balancesDetails === null) {
            return;
        }
        this.bd = rsp.response.balances.balancesDetails;
        this.bs = rsp.response.balances.balancesSummary;
        if( this.deviceType === 'mobile' && rsp.response.balancesList !== null){
            this.bd = rsp.response.balancesList[1].balancesDetails;
            this.bs = rsp.response.balancesList[1].balancesSummary;
        }
        let rowDataArr = BalancesExportUtil.processBalancesDetails(null, this.bd, null, this.bs, 'custodian', this.deviceType, this.balancesService);
        this.rowData = this.sortRowDataArr(rowDataArr);
        this.initChart();
        this.isLoading = false;
        console.log(" this.rowData is ====",  this.rowData);
    }

    sortRowDataArr(ar) {
        let i = 0, j; 
        while (i < ar.length) { 
            j = i + 1; 
            while (j < ar.length) {
                if (ar[j].value > ar[i].value  && ar[i].name != "DETAIL.keyValues.totalAccount" && ar[j].name != "DETAIL.keyValues.totalAccount") {
                    let temp = ar[i]; 
                    ar[i] = ar[j]; 
                    ar[j] = temp; 
                } 
                j++; 
            } 
            i++; 
        }
        return ar;
    } 

    initCustomHeaders() {
        let customHeaders = {};
        let acctTypeHeader: CustomGridHeader[] = [];
        acctTypeHeader.push(
            {
                'name': BalancesLabels.DETAIL_LABELS_BNYMELLONNA,
                'footer': '',
                'className': 'accountType subHeadingDisplay'
            }
        );
        acctTypeHeader.push(
            {
                'name': BalancesLabels.DETAIL_LABELS_BALANCESTODAYCHNG,
                'footer': '',
                'className': 'todaysChange subHeadingDisplay'
            }
        );
        customHeaders[BalancesLabels.DETAIL_LABELS_BNYMELLONNA] = acctTypeHeader;
        this.customHeaders = { 'customHeaders': customHeaders };
    }
    ngOnDestroy(): void {
        this.balancesService.updateDisclaimer({
            'legendSymbol': '', legendByIndex: true, 'key': BalancesLabels.DISCLAIMER_LONGMARKETVALUE,
            'show': false, 'className': 'longMarketValue', 'showCounter': 0
        });
        this.balancesService.updateDisclaimer({
            'legendSymbol': '', 'legendByIndex': true, 'key': BalancesLabels.DISCLAIMER_SECURITYOWED,
            'show': false, 'className': 'securityOwed', 'showCounter': 0
        });
        this.balancesService.updateDisclaimer({
            'legendSymbol': '', 'legendByIndex': true, 'key': BalancesLabels.DISCLAIMER_CASHMGTINT,
            'show': false, 'className': 'cashMgtInt', 'showCounter': 0
        });
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
